import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { v4 as uuidv4 } from "uuid";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import CepInput from "@/components/address/CepInput";
import { concessionarias } from "@/types/index";
import { InverterApi } from "@/types/inverterApi";
import { toast } from "sonner";

const formSchema = z.object({
  nomeTitular: z.string().min(3, { message: "O nome deve ter pelo menos 3 caracteres" }),
  documento: z.string().min(11, { message: "Insira um CPF ou CNPJ válido" }),
  cep: z.string().min(8, { message: "CEP deve ter 8 dígitos" }),
  logradouro: z.string().min(3, { message: "O logradouro deve ter pelo menos 3 caracteres" }),
  bairro: z.string().min(2, { message: "O bairro deve ter pelo menos 2 caracteres" }),
  cidade: z.string().min(2, { message: "A cidade deve ter pelo menos 2 caracteres" }),
  estado: z.string().min(2, { message: "O estado deve ter pelo menos 2 caracteres" }),
  concessionaria: z.string().min(1, { message: "Selecione uma concessionária" }),
  potenciaInstalada: z.coerce.number().min(0.1, { message: "A potência deve ser maior que 0" }),
  geracaoEstimada: z.coerce.number().min(1, { message: "A geração estimada deve ser maior que 0" }),
  tarifaVenda: z.coerce.number().min(0.01, { message: "A tarifa deve ser maior que 0" }),
  custoInvestimento: z.coerce.number().min(1, { message: "O custo deve ser maior que 0" }),
  conectarApi: z.boolean().default(false),
  inverterApiId: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const GeradorForm = () => {
  const navigate = useNavigate();
  const [calculatedValues, setCalculatedValues] = useState({
    valorGeracaoEstimada: 0,
    payback: 0,
    valorKwhMes: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inverterApis, setInverterApis] = useState<InverterApi[]>([]);

  useEffect(() => {
    try {
      const storedApis = JSON.parse(localStorage.getItem("inverterApis") || "[]");
      setInverterApis(storedApis.filter((api: InverterApi) => api.isActive));
    } catch (error) {
      console.error("Error loading APIs:", error);
    }
  }, []);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nomeTitular: "",
      documento: "",
      cep: "",
      logradouro: "",
      bairro: "",
      cidade: "",
      estado: "",
      concessionaria: "",
      potenciaInstalada: 0,
      geracaoEstimada: 0,
      tarifaVenda: 0,
      custoInvestimento: 0,
      conectarApi: false,
      inverterApiId: "",
    },
  });

  const watchPotencia = form.watch("potenciaInstalada");
  const watchGeracao = form.watch("geracaoEstimada");
  const watchTarifa = form.watch("tarifaVenda");
  const watchCusto = form.watch("custoInvestimento");
  const watchConectarApi = form.watch("conectarApi");

  useEffect(() => {
    const valorGeracaoEstimada = watchGeracao * watchTarifa;
    const payback = valorGeracaoEstimada > 0 ? watchCusto / (valorGeracaoEstimada * 12) : 0;
    const valorKwhMes = watchPotencia > 0 ? watchCusto / watchPotencia : 0;

    setCalculatedValues({
      valorGeracaoEstimada,
      payback,
      valorKwhMes,
    });
  }, [watchPotencia, watchGeracao, watchTarifa, watchCusto]);

  const handleCepFound = (address: any) => {
    form.setValue("cep", address.cep);
    form.setValue("logradouro", address.logradouro);
    form.setValue("bairro", address.bairro);
    form.setValue("cidade", address.localidade);
    form.setValue("estado", address.uf);
    
    toast.success("Endereço encontrado com sucesso!");
  };

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      const geradorId = uuidv4();

      const gerador = {
        id: geradorId,
        ...values,
        valorGeracaoEstimada: calculatedValues.valorGeracaoEstimada,
        payback: calculatedValues.payback,
        valorKwhMes: calculatedValues.valorKwhMes,
        creditosDisponiveis: values.geracaoEstimada,
        receitaTotal: 0,
        criadoPor: "current-user-id",
      };

      const geradores = JSON.parse(localStorage.getItem("geradores") || "[]");
      localStorage.setItem("geradores", JSON.stringify([...geradores, gerador]));

      if (values.conectarApi && values.inverterApiId) {
        toast.info(`Iniciando conexão com a API do inversor para o gerador ${values.nomeTitular}`);
      }

      toast.success(`O gerador ${values.nomeTitular} foi cadastrado com sucesso.`);

      navigate("/geradores");
    } catch (error) {
      console.error("Erro ao cadastrar gerador:", error);
      toast.error("Ocorreu um erro ao cadastrar o gerador. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Tabs defaultValue="dados" className="w-full">
        <TabsList className="bg-green-50 border border-green-100">
          <TabsTrigger value="dados" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
            Dados do Gerador
          </TabsTrigger>
          <TabsTrigger value="financeiro" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
            Dados Financeiros
          </TabsTrigger>
          <TabsTrigger value="integracao" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
            Integração
          </TabsTrigger>
        </TabsList>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <TabsContent value="dados">
              <Card className="border-green-100 shadow-sm">
                <CardHeader className="bg-green-50 border-b border-green-100">
                  <CardTitle className="text-green-800">Dados do Gerador</CardTitle>
                  <CardDescription>
                    Informações básicas sobre o gerador e sua localização
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="nomeTitular"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nome do Titular</FormLabel>
                          <FormControl>
                            <Input placeholder="Nome completo" {...field} className="border-green-200 focus-visible:ring-green-500" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="documento"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>CPF/CNPJ</FormLabel>
                          <FormControl>
                            <Input placeholder="000.000.000-00" {...field} className="border-green-200 focus-visible:ring-green-500" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator className="my-6 bg-green-100" />

                  <div className="bg-green-50 p-4 rounded-md border border-green-100 space-y-4">
                    <h3 className="text-sm font-medium text-green-800 mb-2">Endereço do Gerador</h3>
                    
                    <FormField
                      control={form.control}
                      name="cep"
                      render={({ field }) => (
                        <FormItem className="w-full md:w-1/3">
                          <FormLabel>CEP</FormLabel>
                          <FormControl>
                            <CepInput 
                              value={field.value} 
                              onChange={field.onChange} 
                              onAddressFound={handleCepFound}
                              className="border-green-200 focus-visible:ring-green-500"
                            />
                          </FormControl>
                          <FormDescription className="text-xs">
                            Digite o CEP para buscar o endereço automaticamente
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid gap-4 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="logradouro"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Logradouro</FormLabel>
                            <FormControl>
                              <Input placeholder="Rua, Avenida, etc" {...field} className="border-green-200 focus-visible:ring-green-500" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="bairro"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Bairro</FormLabel>
                            <FormControl>
                              <Input placeholder="Bairro" {...field} className="border-green-200 focus-visible:ring-green-500" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="cidade"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Cidade</FormLabel>
                            <FormControl>
                              <Input placeholder="Cidade" {...field} className="border-green-200 focus-visible:ring-green-500" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="estado"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Estado</FormLabel>
                            <FormControl>
                              <Input placeholder="UF" {...field} maxLength={2} className="border-green-200 focus-visible:ring-green-500" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="w-full md:w-1/2 mt-4">
                    <FormField
                      control={form.control}
                      name="concessionaria"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Concessionária</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="border-green-200 focus-visible:ring-green-500">
                                <SelectValue placeholder="Selecionar concessionária" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {concessionarias.map((concessionaria) => (
                                <SelectItem
                                  key={concessionaria}
                                  value={concessionaria}
                                >
                                  {concessionaria}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="financeiro">
              <Card className="border-green-100 shadow-sm">
                <CardHeader className="bg-green-50 border-b border-green-100">
                  <CardTitle className="text-green-800">Dados Financeiros</CardTitle>
                  <CardDescription>
                    Informações financeiras e estimativas de geração
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="potenciaInstalada"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Potência Instalada (kWp)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="0"
                              {...field}
                              step="0.01"
                              className="border-green-200 focus-visible:ring-green-500"
                              decimalPrecision={2}
                            />
                          </FormControl>
                          <FormDescription>
                            Potência total do sistema em kWp
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="geracaoEstimada"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Geração Mensal Estimada (kWh)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="0"
                              {...field}
                              step="1"
                              className="border-green-200 focus-visible:ring-green-500"
                              decimalPrecision={0}
                            />
                          </FormControl>
                          <FormDescription>
                            Estimativa mensal de geração em kWh
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="tarifaVenda"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tarifa de Venda (R$/kWh)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="0.00"
                              {...field}
                              step="0.01"
                              className="border-green-200 focus-visible:ring-green-500"
                              decimalPrecision={4}
                            />
                          </FormControl>
                          <FormDescription>
                            Valor cobrado por kWh vendido
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="custoInvestimento"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Custo do Investimento (R$)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="0.00"
                              {...field}
                              step="0.01"
                              className="border-green-200 focus-visible:ring-green-500"
                              decimalPrecision={2}
                            />
                          </FormControl>
                          <FormDescription>
                            Custo total do investimento
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator className="my-6 bg-green-100" />

                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-md border border-green-200 bg-green-50 p-4">
                      <h3 className="text-sm font-medium text-green-800 mb-2">
                        Valor Mensal Estimado
                      </h3>
                      <div className="text-2xl font-bold text-green-700">
                        R$ {calculatedValues.valorGeracaoEstimada.toFixed(2)}
                      </div>
                      <p className="text-xs text-green-600 mt-1">
                        Geração x Tarifa
                      </p>
                    </div>

                    <div className="rounded-md border border-green-200 bg-green-50 p-4">
                      <h3 className="text-sm font-medium text-green-800 mb-2">
                        Payback (anos)
                      </h3>
                      <div className="text-2xl font-bold text-green-700">
                        {calculatedValues.payback.toFixed(1)}
                      </div>
                      <p className="text-xs text-green-600 mt-1">
                        Tempo de retorno do investimento
                      </p>
                    </div>

                    <div className="rounded-md border border-green-200 bg-green-50 p-4">
                      <h3 className="text-sm font-medium text-green-800 mb-2">
                        Valor R$/kWp
                      </h3>
                      <div className="text-2xl font-bold text-green-700">
                        R$ {calculatedValues.valorKwhMes.toFixed(2)}
                      </div>
                      <p className="text-xs text-green-600 mt-1">
                        Custo por kWp instalado
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="integracao">
              <Card className="border-green-100 shadow-sm">
                <CardHeader className="bg-green-50 border-b border-green-100">
                  <CardTitle className="text-green-800">Integração com API</CardTitle>
                  <CardDescription>
                    Configure a integração com a API do fabricante do inversor
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                  <FormField
                    control={form.control}
                    name="conectarApi"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-green-200 bg-green-50 p-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="border-green-400 text-green-600 focus-visible:ring-green-500"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-green-800">Conectar a API do Fabricante</FormLabel>
                          <FormDescription>
                            Habilitar conexão em tempo real com a API do fabricante do inversor
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  {watchConectarApi && (
                    <div className="mt-4">
                      {inverterApis.length === 0 ? (
                        <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4">
                          <p className="text-yellow-800">
                            Nenhuma API configurada. Por favor, adicione uma API no menu 
                            <Button variant="link" asChild className="p-0 h-auto text-yellow-800 font-semibold">
                              <Link to="/solar/apis"> APIs de Inversores</Link>
                            </Button>.
                          </p>
                        </div>
                      ) : (
                        <FormField
                          control={form.control}
                          name="inverterApiId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Selecione a API</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger className="border-green-200 focus-visible:ring-green-500">
                                    <SelectValue placeholder="Selecionar API" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {inverterApis.map((api) => (
                                    <SelectItem key={api.id} value={api.id}>
                                      {api.manufacturer} - {api.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormDescription>
                                Selecione a API do fabricante para integração
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <div className="mt-6 flex justify-end gap-4">
              <Button 
                variant="outline" 
                type="button" 
                onClick={() => navigate("/dashboard")}
                className="border-green-200 hover:bg-green-50 text-green-700"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {isSubmitting ? "Cadastrando..." : "Cadastrar Gerador"}
              </Button>
            </div>
          </form>
        </Form>
      </Tabs>
    </div>
  );
};

export default GeradorForm;
